import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react'
import { Link, type LinkProps } from 'react-router-dom'

type Variant = 'primary' | 'ghost' | 'outline' | 'text' | 'danger'

const VARIANT_CLASSES: Record<Variant, string> = {
  primary: 'bg-[var(--accent)] text-white border border-[var(--accent)] hover:bg-[var(--accent-hover)] hover:border-[var(--accent-hover)]',
  ghost:
    'bg-transparent text-[var(--text-secondary)] border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent-text)]',
  outline:
    'bg-transparent text-[var(--accent-text)] border border-[var(--accent)] hover:bg-[var(--accent-subtle)]',
  text: 'bg-transparent text-[var(--accent)] border-none hover:text-[var(--accent-hover)] font-medium',
  // The prototype's destructive confirm (`#admin-confirm-ok-btn`): `--danger`
  // ground, white text — the one button an admin confirmation paints red.
  danger: 'bg-[var(--danger)] text-white border border-[var(--danger)] hover:brightness-95',
}

const BASE_CLASSES =
  'inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold transition-colors'

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...props}
      className={`${BASE_CLASSES} disabled:opacity-40 disabled:cursor-not-allowed ${VARIANT_CLASSES[variant]} ${className}`}
    />
  )
}

/** A router link styled exactly like `Button` — for an action that only
 * navigates, so it keeps link semantics (an href, open-in-new-tab, announced
 * as a link) instead of being a button with an onClick navigate. */
export function LinkButton({
  variant = 'primary',
  className = '',
  ...props
}: LinkProps & { variant?: Variant }) {
  return (
    <Link
      {...props}
      className={`${BASE_CLASSES} no-underline ${VARIANT_CLASSES[variant]} ${className}`}
    />
  )
}

/** A plain `<a>` styled exactly like `Button` — for a full-page navigation
 * to a backend route (e.g. the Cognito sign-in start, MS-977), which a router
 * `Link` would wrongly try to handle client-side, and which also has to work
 * where there is no router at all (Login renders straight from App's Gate). */
export function AnchorButton({
  variant = 'primary',
  className = '',
  children,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: Variant }) {
  return (
    <a
      {...props}
      className={`${BASE_CLASSES} no-underline ${VARIANT_CLASSES[variant]} ${className}`}
    >
      {children}
    </a>
  )
}
